import { useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useGameStore } from '@/stores/gameStore';
import { useCurrentSession } from '@/stores/gameSessionStore';
import { useSessionRecovery } from '@/utils/sessionRecovery';
import CountdownScreen from '@/components/CountdownScreen';
import MobileGameOverlay from '@/components/MobileGameOverlay';
import useSessionPhaseTimer from '@/hooks/useSessionPhaseTimer';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';

const CountdownPage = () => {
  const navigate = useSafeNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const currentSession = useCurrentSession();
  const activeSession = sessionId ? currentSession : null;
  const { getStoredSessionData } = useSessionRecovery();
  const {
    getCurrentQuestion,
    gameState,
    setGameState,
    questions,
    initializeGame,
    hydrateSessionPlayers,
    setCurrentPlayerId
  } = useGameStore();

  const currentQuestion = getCurrentQuestion();
  const playerIdFromParams = searchParams.get('playerId');
  const fallbackCountdownSeconds = (() => {
    const configuredDuration = activeSession?.currentPhaseDurationSeconds;
    if (typeof configuredDuration === 'number' && configuredDuration > 0) {
      return configuredDuration;
    }

    return 3;
  })();
  const countdownTimeRemaining = useSessionPhaseTimer(activeSession, fallbackCountdownSeconds);
  const isTeacherControlledSession = Boolean(sessionId);
  const hasRemoteCountdownTiming = Boolean(
    activeSession?.gameState === 'countdown' &&
    (
      activeSession.currentPhaseEndTime ||
      (activeSession.currentPhaseStartTime && activeSession.currentPhaseDurationSeconds)
    )
  );

  // Debug logging for CountdownPage state

  // Check if game is properly initialized, with intelligent recovery
  useEffect(() => {
    if (questions.length === 0) {
      console.warn('⚠️ CountdownPage: No questions found');

      // If we have a current session, try to initialize from session data
      if (activeSession && activeSession.questions.length > 0) {
        initializeGame(activeSession.id, activeSession.questions);
        return;
      }

      // If we have stored session data, try recovery
      const storedData = getStoredSessionData();
      if (storedData.canRecover && sessionId) {
        return;
      }

      // Only redirect if we're absolutely sure there's no way to recover
      if (!sessionId && !activeSession) {
        console.warn('⚠️ CountdownPage: No session data available, redirecting to lobby');
        navigate('/lobby');
      }
    }
  }, [questions.length, activeSession, sessionId, initializeGame, getStoredSessionData, navigate]);

  useEffect(() => {
    if (activeSession && activeSession.players) {
      hydrateSessionPlayers(activeSession.players, playerIdFromParams || null);
      setCurrentPlayerId(playerIdFromParams || null);
    }
  }, [activeSession?.players, hydrateSessionPlayers, setCurrentPlayerId, playerIdFromParams, activeSession]);

  // Set game state to countdown when component mounts
  useEffect(() => {
    if (isTeacherControlledSession) {
      return;
    }

    if (gameState !== 'countdown' && currentQuestion) {
      setGameState('countdown');
    }
  }, [currentQuestion, gameState, setGameState, isTeacherControlledSession]);

  const handleCountdownComplete = () => {
    if (sessionId) {
      return;
    }

    setGameState('question');
    navigate(`/question?sessionId=${sessionId}&playerId=${searchParams.get('playerId') || ''}`);
  };

  if (!currentQuestion) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '3rem',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔄</div>
          <h2 style={{ fontSize: '1.8rem', margin: 0 }}>Loading game...</h2>
        </div>
      </div>
    );
  }

  if (isTeacherControlledSession && (!activeSession || !hasRemoteCountdownTiming)) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '3rem',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏱️</div>
          <h2 style={{ fontSize: '1.8rem', margin: 0 }}>Syncing countdown...</h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <MobileGameOverlay />
      <CountdownScreen
        isTeacher={false}
        onCountdownComplete={handleCountdownComplete}
        sessionId={sessionId || undefined}
        playerId={searchParams.get('playerId') || undefined}
        timeRemaining={isTeacherControlledSession ? countdownTimeRemaining : undefined}
      />
    </>
  );
};

export default CountdownPage;
